import React, { useState } from 'react';
import { Table, Typography, Checkbox, Button, Input, Tag, Tooltip } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './StandsByCustomerPivotTable.css';
import useStandStore from '../store/standStore';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authUserStore';

export interface CheckListItem {
    id: string;
    name: string;
    sector: string | null;
    stand: string;
    key: string;
    signature: string;
    orders?: {
        orderItems?: {
            product: {
                id: string;
                name: string;
                type: string;
                quantityPerBox: number;
            };
            issuedItems?: {
                totalQuantity: number;
                multiplier: number;
            };
        }[];
    }[];
    lastOrder?: {
        signature?: string;
        isOrderConfirmedWithoutSignature?: boolean;
        user?: {
            id: string;
            username?: string;
        };
    };
}

interface Customer {
    id: string;
    name: string;
    stands?: CheckListItem[];
}

interface Event {
    id: string;
    name: string;
    items?: {
        id: string;
        name: string;
        type: string;
        itemsPerBox: number;
    }[];
}

interface StandsByCustomerPivotTableProps {
    event: Event;
    customers: Customer[];
}

const StandsByCustomerPivotTable: React.FC<StandsByCustomerPivotTableProps> = ({ event, customers }) => {
    const { updateItem, stands, setStands } = useStandStore();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { currentUser } = useAuthStore();
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // Track local notes edits per stand
    const [notesDraft, setNotesDraft] = useState<{ [standId: string]: string }>({});
    const [notesChanged, setNotesChanged] = useState<{ [standId: string]: boolean }>({});

    React.useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const handleDeliveryDoneChange = async (standId: string, checked: boolean) => {
        try {
            await updateItem(standId, { isDeliveryDone: checked });
            const updatedStands = stands.map((stand) =>
                stand.id === standId ? { ...stand, isDeliveryDone: checked } : stand
            );
            setStands(updatedStands);
        } catch (error) {
            console.error('Error updating delivery status:', error);
        }
    };

    const handleRowClick = (record: any) => {
        navigate(`/stands/details/${record.key}`);
    };

    const handleOrderConfirmationNotesChange = (standId: string, value: string) => {
        setNotesDraft((prev) => ({ ...prev, [standId]: value }));
        setNotesChanged((prev) => ({ ...prev, [standId]: true }));
    };

    const handleSaveNotes = async (standId: string) => {
        const value = notesDraft[standId];
        try {
            await updateItem(standId, { orderConfirmationNotes: value });
            const updatedStands = stands.map((stand) =>
                stand.id === standId ? { ...stand, orderConfirmationNotes: value } : stand
            );
            setStands(updatedStands);
            setNotesChanged((prev) => ({ ...prev, [standId]: false }));
        } catch (error) {
            console.error('Error updating order confirmation notes:', error);
        }
    };

    // check if current user is the last order user for a stand
    const isCurrentUserLastOrderUser = (stand: any) => {
        return (
            currentUser &&
            stand.lastOrder &&
            stand.lastOrder.user &&
            stand.lastOrder.user.id === currentUser.id
        );
    };

    const columns = [
        {
            title: t('checkList.customerInfo'),
            children: [
                {
                    title: t('checkList.customer'),
                    dataIndex: 'customer',
                    key: 'customer',
                    render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
                },
                {
                    title: t('checkList.sector'),
                    dataIndex: 'sector',
                    key: 'sector',
                },
                {
                    title: t('checkList.stand'),
                    dataIndex: 'stand',
                    key: 'stand',
                },
                {
                    title: t('checkList.lastOrderBy'),
                    dataIndex: 'lastOrderUser',
                    key: 'lastOrderUser',
                    render: (text: string) => text || '-',
                },
            ],
        },
        {
            title: event.name,
            children: event.items?.map((item, index) => ({
                title: (
                    <div className="pivot-header">
                        <Typography.Text strong>{item.name}</Typography.Text>
                        <br />
                        <Typography.Text>{item.type}</Typography.Text>
                        <br />
                        <Typography.Text>{item.itemsPerBox} {t('checkList.perBox')}</Typography.Text>
                    </div>
                ),
                children: [
                    {
                        title: t('checkList.issuedItems'),
                        dataIndex: `${item.id}_issuedItems`,
                        key: `${item.id}_issuedItems`,
                        align: 'right',
                        className: `number ${index % 2 === 0 ? 'even-column' : 'odd-column'}`,
                    },
                    {
                        title: t('checkList.issuedBoxes'),
                        dataIndex: `${item.id}_issuedBoxes`,
                        key: `${item.id}_issuedBoxes`,
                        align: 'right',
                        className: `number ${index % 2 === 0 ? 'even-column' : 'odd-column'}`,
                    },
                ],
            })) || [],
        },
        {
            title: t('checkList.verification'),
            children: [
                {
                    title: t('checkList.deliveryDone'),
                    dataIndex: 'isDeliveryDone',
                    key: 'isDeliveryDone',
                    render: (value: boolean, record: any) => (
                        <div style={{ width: '8em', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto' }}>
                            <Checkbox
                                checked={value}
                                disabled={!isCurrentUserLastOrderUser(record)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleDeliveryDoneChange(record.key, e.target.checked)}
                            />
                        </div>
                    ),
                    align: 'center',
                },

                {
                    title: t('checkList.hasImages'),
                    dataIndex: 'hasImages',
                    key: 'hasImages',
                    className: 'verification-column',
                    render: (_: any, record: any) => (
                        <div style={{ display: 'flex', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                            <Tooltip title={t('checkList.hasImages')}>
                                <Checkbox checked={!!record._hasImages} disabled />
                            </Tooltip>
                        </div>
                    ),
                    align: 'center',
                },

                {
                    title: t('checkList.allSigned'),
                    dataIndex: 'allSigned',
                    key: 'allSigned',
                    className: 'verification-column',
                    render: (_: any, record: any) => (
                        <div style={{ display: 'flex', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                            <Tooltip title={t('checkList.allSigned')}>
                                <Checkbox checked={!!record._allSigned} disabled />
                            </Tooltip>
                        </div>
                    ),
                    align: 'center',
                },

                {
                    title: t('checkList.orderConfirmationNotes'),
                    dataIndex: 'orderConfirmationNotes',
                    key: 'orderConfirmationNotes',
                    render: (value: string, record: any) => {
                        const standId = record.key;
                        const localValue = notesDraft[standId] !== undefined ? notesDraft[standId] : value;
                        const changed = notesChanged[standId] && localValue !== value && localValue !== undefined;
                        return (
                            <div style={{ minWidth: 120, position: 'relative' }}>
                                <Input.TextArea
                                    value={localValue}
                                    placeholder={t('checkList.notes')}
                                    autoSize={{ minRows: 1, maxRows: 3 }}
                                    disabled={!isCurrentUserLastOrderUser(record)}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => handleOrderConfirmationNotesChange(standId, e.target.value)}
                                    style={{ minWidth: 80, maxWidth: 180 }}
                                />
                                {changed && isCurrentUserLastOrderUser(record) && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleSaveNotes(standId);
                                            }}
                                        >
                                            {t('general.submit') || 'Save'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    },
                    align: 'center',
                },
            ],
        },
    ];

    const sortedStands = stands
        .slice()
        .sort((a, b) => {
            const cA = (a.customer?.companyName || '').toLowerCase();
            const cB = (b.customer?.companyName || '').toLowerCase();
            if (cA !== cB) return cA.localeCompare(cB);
            return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
        });

    // Assign group index per unique customer (for alternating row backgrounds)
    let groupIndex = 0;
    let lastCustomerId = '';
    const data = sortedStands.map((stand) => {
        const customerId = stand.customer?.id || '';
        if (customerId !== lastCustomerId) {
            if (lastCustomerId !== '') groupIndex++;
            lastCustomerId = customerId;
        }
        const isSigned = !!stand.lastOrder?.signature || !!stand.lastOrder?.isOrderConfirmedWithoutSignature;
        const canSign = !isSigned && currentUser && stand.lastOrder?.user?.id === currentUser.id;
        const billOrders = (stand.orders || []).filter(o => o.billType === 'deliverynote' || o.billType === 'redemptionslip');
        const hasImages = billOrders.some(o => !!(o as any).image);
        const allSigned = billOrders.length > 0 && billOrders.every(o => !!(o as any).signature || !!(o as any).isOrderConfirmedWithoutSignature);
        const row: any = {
            key: stand.id,
            customer: stand.customer?.companyName || 'Unknown',
            sector: stand.sector?.name || '-',
            stand: stand.name,
            lastOrderUser: stand.lastOrder?.user?.username || '-',
            lastOrder: stand.lastOrder,
            isDeliveryDone: stand.isDeliveryDone || false,
            _isSigned: isSigned,
            _canSign: canSign,
            _hasImages: hasImages,
            _allSigned: allSigned,
            _customerGroupIndex: groupIndex,
        };

        event.items?.forEach((item) => {
            const issuedItems = stand.orders?.flatMap(order =>
                order.orderItems?.filter((orderItem) => orderItem.product.id === item.id) || []
            ) || [];

            row[`${item.id}_issuedItems`] = issuedItems.reduce((total, orderItem) => total + (orderItem.issuedItems?.totalQuantity || 0), 0);
            row[`${item.id}_issuedBoxes`] = issuedItems.reduce((total, orderItem) => total + (orderItem.issuedItems?.multiplier || 0), 0);
        });

        return row;
    });

    const totals: Record<string, number> = {};
    stands.forEach((stand) => {
        event.items?.forEach((item) => {
            const issuedKey = `${item.id}_issuedItems`;
            const boxesKey = `${item.id}_issuedBoxes`;
            totals[issuedKey] = (totals[issuedKey] || 0) + (stand.orders?.flatMap(order =>
                order.orderItems?.filter(orderItem => orderItem.product.id === item.id) || []
            ).reduce((total, orderItem) => total + (orderItem.issuedItems?.totalQuantity || 0), 0) || 0);
            totals[boxesKey] = (totals[boxesKey] || 0) + (stand.orders?.flatMap(order =>
                order.orderItems?.filter(orderItem => orderItem.product.id === item.id) || []
            ).reduce((total, orderItem) => total + (orderItem.issuedItems?.multiplier || 0), 0) || 0);
        });
    });

    if (!isOnline) {
        return (
            <div style={{ padding: 32, textAlign: 'center', color: '#bfbfbf', fontSize: 18 }}>
                {t('checkList.onlyOnline')}
            </div>
        );
    }

    return (
        <div className="checklist-shell" data-tour="checklist-table">
            <div className="checklist-legend" aria-hidden="true">
                <span className="checklist-badge checklist-badge-signed">{t('checkList.signed')}</span>
                <span className="checklist-badge checklist-badge-signable">{t('checkList.sign')}</span>
            </div>
            <Table
                columns={columns}
                dataSource={data} // use data from all stands
                pagination={{ pageSize: 12 }}
                bordered
                className="pivot-table checklist-table"
                scroll={{ x: 'max-content', y: 500 }}
                title={() => (
                    <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 18, padding: '8px 0' }}>
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            {event.name ? `${event.name} - ${customers.length} ${t('checkList.standOperators')}` : t('checkList.standsByCustomers')}
                        </Typography.Title>
                    </div>
                )}
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    className: [
                        record._isSigned ? 'signed-row' : record._canSign ? 'signable-row' : '',
                        record._customerGroupIndex % 2 === 0 ? 'customer-group-even' : 'customer-group-odd',
                    ].filter(Boolean).join(' '),
                })}
                summary={pageData => {
                    if (!event.items) return null;
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={3}>
                                    <b>Total</b>
                                </Table.Summary.Cell>
                                {event.items.map((item, idx) => (
                                    <React.Fragment key={item.id}>
                                        <Table.Summary.Cell
                                            index={3 + idx * 2}
                                            align="right"
                                            className={totals[`${item.id}_issuedItems`] > 0 ? 'positive-total' : ''}
                                        >
                                            <b>{totals[`${item.id}_issuedItems`] || 0}</b>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                            index={4 + idx * 2}
                                            align="right"
                                            className={totals[`${item.id}_issuedBoxes`] > 0 ? 'positive-total' : ''}
                                        >
                                            <b>{totals[`${item.id}_issuedBoxes`] || 0}</b>
                                        </Table.Summary.Cell>
                                    </React.Fragment>
                                ))}
                                <Table.Summary.Cell index={999} colSpan={columns[2].children.length}></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
                sticky
            />
        </div>
    );
};

export default StandsByCustomerPivotTable;
